import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Loader2, TrendingUp, BookMarked, IndianRupee, Upload } from "lucide-react";

export default function SellerDashboard() {
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ total_notes: 0, total_sales: 0, total_earnings: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ title: "", description: "", subject: "", price: "", pages: "", level: "Professional Certification" });
  const [pdf, setPdf] = useState(null);
  const [cover, setCover] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, s] = await Promise.all([api.get("/seller/notes"), api.get("/seller/stats")]);
      setNotes(n.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pdf) {
      toast.error("Please attach a PDF file");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("subject", form.subject);
      fd.append("price", form.price);
      fd.append("level", form.level);
      if (form.pages) fd.append("pages", form.pages);
      fd.append("pdf", pdf);
      if (cover) fd.append("cover", cover);
      await api.post("/notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Note listed successfully!");
      setOpen(false);
      setForm({ title: "", description: "", subject: "", price: "", pages: "", level: "Professional Certification" });
      setPdf(null);
      setCover(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to list note");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      toast.success("Note removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900">Seller Dashboard</h1>
          <p className="text-muted-foreground mt-1">Upload and manage the notes you sell.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2" data-testid="new-note-button">
              <Plus className="h-4 w-4" /> List new note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">List a new note</DialogTitle>
              <DialogDescription>Upload your PDF and set a price. Buyers get instant access after purchase.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1.5" data-testid="form-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" required placeholder="e.g. Biology" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-1.5" data-testid="form-subject" />
                </div>
                <div>
                  <Label htmlFor="price">Price (₹)</Label>
                  <Input id="price" type="number" min="1500" step="1" required value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="min 1500"
                    className="mt-1.5" data-testid="form-price" />
                </div>
              </div>
              <div>
                <Label htmlFor="level">Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="form-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Postgraduate (Masters)">Postgraduate (Masters)</SelectItem>
                    <SelectItem value="PhD / Doctoral">PhD / Doctoral</SelectItem>
                    <SelectItem value="Professional Certification">Professional Certification</SelectItem>
                    <SelectItem value="Undergraduate">Undergraduate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pages">Pages (optional)</Label>
                <Input id="pages" type="number" min="1" value={form.pages}
                  onChange={(e) => setForm({ ...form, pages: e.target.value })}
                  className="mt-1.5" data-testid="form-pages" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" required rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5" data-testid="form-description" />
              </div>
              <div>
                <Label htmlFor="pdf">Notes PDF *</Label>
                <Input id="pdf" type="file" accept="application/pdf"
                  onChange={(e) => setPdf(e.target.files[0])}
                  className="mt-1.5" data-testid="form-pdf" />
              </div>
              <div>
                <Label htmlFor="cover">Cover image (optional)</Label>
                <Input id="cover" type="file" accept="image/*"
                  onChange={(e) => setCover(e.target.files[0])}
                  className="mt-1.5" data-testid="form-cover" />
              </div>
              <DialogFooter>
                <Button type="submit" className="rounded-full gap-2 w-full" disabled={submitting} data-testid="form-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Publish note</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <StatCard icon={BookMarked} label="Notes listed" value={stats.total_notes} />
        <StatCard icon={TrendingUp} label="Total sales" value={stats.total_sales} />
        <StatCard icon={IndianRupee} label="Earnings" value={`₹${stats.total_earnings}`} />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl" data-testid="seller-empty">
          <p className="text-lg font-medium text-slate-900">No notes yet</p>
          <p className="text-muted-foreground mt-1">Click "List new note" to start selling.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="seller-notes">
          {notes.map((n) => (
            <div key={n.id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-white" data-testid={`seller-note-${n.id}`}>
              <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-white/80" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 line-clamp-1">{n.title}</p>
                <p className="text-xs text-muted-foreground">{n.subject} · ₹{n.price}</p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-900">{n.sold_count} sold</p>
                <p className="text-xs text-emerald-600">₹{n.earnings} earned</p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove this note?</AlertDialogTitle>
                    <AlertDialogDescription>
                      "{n.title}" will be removed from the marketplace. Buyers who already purchased keep access.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del(n.id)} className="bg-destructive hover:bg-destructive/90">
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="p-5 rounded-2xl border border-border bg-white flex items-center gap-4">
    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-overline text-muted-foreground">{label}</p>
      <p className="font-heading font-bold text-2xl text-slate-900">{value}</p>
    </div>
  </div>
);
